import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setOrgLogo,
  setQuizGroupId,
  setTheme,
  setUserDetails,
  setUserLogin,
} from "@src/app/globalSlice";
import { RootState } from "@src/app/store";
import Modal from "@src/components/modal/Modal";
import CommonLoader from "@src/components/loader/CommonLoader";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import { useMulti_rolesMutation } from "@src/api/LoginApiSlice";
import { useQuizGroupUrlMutation } from "@src/api/QuizGroupApiSlice";
import { useUpdateStudentCurrentOrgMutation } from "@src/api/UserApiSlice";
import { toast, Toastify } from "top-brains-ui";

const JoinGroupHandler = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const accessToken = localStorage.getItem("accessToken");
  const isAuthenticated = !!accessToken;

  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails
  );

  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [multiRolesUpdate] = useMulti_rolesMutation();
  const [joinGroup] = useQuizGroupUrlMutation();
  const [updateStudentOrg] = useUpdateStudentCurrentOrgMutation();
  const [loading, setLoading] = useState(true);


  useEffect(()=>{
   dispatch(setTheme("app-theme"));
  },[])

  const hasRunRef = useRef(false);
  useEffect(() => {
    if (!groupId) return;

    dispatch(setQuizGroupId(groupId));

    if (!isAuthenticated) {
      navigate("/login", {
        state: { from: `/join-group/${groupId}`, showJoinSuccess: true },
        replace: true,
      });
      dispatch(setUserLogin(true));
      return;
    }

    if (!userDetails?.id || !userDetails?.privileges) {
      const timer = setTimeout(() => {
        if (!userDetails?.id) {
          toast.error("User details not loaded. Please try again.");
          navigate("/");
        }
      }, 5000);
      return () => clearTimeout(timer);
    }

    const handleJoinGroupFlow = async () => {
      const { privileges, currentPrivilege, ...rest } = userDetails;

      if (
        privileges.includes(USER_CONSTANTS.STUDENT) &&
        currentPrivilege !== USER_CONSTANTS.STUDENT
      ) {
        try {
          const response = await multiRolesUpdate({
            currentPrivilege: USER_CONSTANTS.STUDENT,
            userId: userDetails.id,
          });
          dispatch(setOrgLogo(response?.data?.response?.logo as string));
          dispatch(
            setUserDetails({
              ...rest,
              privileges,
              currentPrivilege: USER_CONSTANTS.STUDENT,
            })
          );
          return;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
          toast.error("Failed to switch to student role.");
          setLoading(false);
          return;
        }
      }

      if (
        privileges.includes(USER_CONSTANTS.STUDENT) &&
        currentPrivilege === USER_CONSTANTS.STUDENT
      ) {
        try {
          const joinResponse = await joinGroup({ groupId }).unwrap();
          const matchedOrgId =
            joinResponse?.response?.matchedOrganizationIds?.[0];

          if (!matchedOrgId) {
            throw new Error("No matching organization found.");
          }

          const response = await updateStudentOrg({
            organizationId: matchedOrgId,
          }).unwrap();
          dispatch(setOrgLogo(response?.response?.logo));
          navigate("/activities/quizGroup", {
            state: { groupId, showJoinSuccess: true },
          });
        } catch (err) {
          const error = err as { data?: { response?: string } };
          const errorMsg =
            error?.data?.response ||
            "Failed to join group or set organization.";
          setErrorMessage(errorMsg);
          setShowErrorModal(true);
        }
      } else {
        setShowAccessModal(true);
      }

      setLoading(false);
    };

    if (!hasRunRef.current) {
      hasRunRef.current = true;
      handleJoinGroupFlow();
    }
  }, [groupId, isAuthenticated, userDetails]);

  return (
    <>
      <Toastify />
      {loading && <CommonLoader />}
      {showAccessModal && (
        <Modal
          isOpen={showAccessModal}
          headerTitle="Access Denied"
          height={151}
          body={
            <div style={{ padding: "10px 0" }}>
              <p>Only students can join the group through this link.</p>
            </div>
          }
          primaryButtonText="Go to Home"
          onPrimaryButtonClick={() => {
            setShowAccessModal(false);
            navigate("/");
          }}
          showSecondaryButton={false}
          onClose={() => {
            setShowAccessModal(false);
            navigate("/");
          }}
        />
      )}
      {showErrorModal && (
        <Modal
          isOpen={showErrorModal}
          headerTitle="Error Joining Group"
          height={151}
          body={
            <div style={{ padding: "10px 0" }}>
              <p>{errorMessage}</p>
            </div>
          }
          primaryButtonText="Go to Home"
          onPrimaryButtonClick={() => {
            setShowErrorModal(false);
            navigate("/");
          }}
          showSecondaryButton={false}
          onClose={() => {
            setShowErrorModal(false);
            navigate("/");
          }}
        />
      )}
    </>
  );
};

export default JoinGroupHandler;
